import { mediaQueries } from '@everlywell/leaves';
import { Cart } from 'components/Cart';
import { StaticQuery, graphql } from 'gatsby';
import { useFlags } from 'gatsby-plugin-launchdarkly';
import React, { useEffect, useState, SyntheticEvent, Dispatch } from 'react';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { Flags } from 'utils/constants/featureFlags';
import {
  LineItem,
  Order,
  FormFieldType,
  CartStateType,
  ReducerActionType,
  Variant,
} from 'utils/types';
import useMediaQuery from 'utils/useMediaQuery';

import {
  getLineItems,
  getCurrentOrder,
  addLineItem,
  removeLineItem,
  editLineItem,
  addPromoToOrder,
  removePromos,
  upgradeLineItem,
} from '../../utils/cartHelpers';
import {
  hasSubscriptionItemInCart,
  hasLabVisitItemInCart,
} from '../../utils/helpers/paymentHelpers';
import {
  setPromoErrors,
  setCartErrors,
  setLineItems,
  setCurrentOrder,
  setCartTotal,
  setIsOpen,
  setIsPromoApplied,
  setCurrentPromoCode,
  setPromoInputValue,
  setShouldShowPromoInput,
  setShowTOS,
} from './store/actions';

type Props = {
  newProduct?: LineItem;
  productToRemove?: LineItem;
  changeProductQuantity?: Function;
  productToChange?: LineItem;
  cartState: CartStateType;
  dispatch: Dispatch<ReducerActionType>;
};

/**
 * code specific to the FS -> FSC Upgrade A/B Test
 */
const backgroundImageUrl =
  '//images.ctfassets.net/ydyvnem5zkxh/QiZ8HVyesazwdhfN5lhVA/33c4a331224e358ef9e577ea40e4c515/adobe-stock-263459945_3x.png?w=800&q=75';

const upsellCardContent = {
  textAccent: 'Want to test 108 more foods?',
  title: 'Food Sensitivity Comprehensive Test',
  description: 'Check your immune response to an even broader set of foods.',
  buttonText: 'Upgrade',
  upgradeCost: '+$100.00',
  backgroundImageUrl: backgroundImageUrl,
};

const getFSLineItem = (lineItems: LineItem[]): LineItem | undefined => {
  return lineItems.find((lineItem) => {
    return /^Food Sensitivity Test$/.test(lineItem.product_name || '');
  });
};

const includesFSLineItem = (lineItems: LineItem[]): boolean => {
  return lineItems.some((lineItem) =>
    /^Food Sensitivity Test$/.test(lineItem.product_name || ''),
  );
};

const includesFSCLineItem = (
  lineItems: LineItem[],
  fscVariantId: number,
): boolean =>
  lineItems.some((lineItem: LineItem) => {
    return lineItem.variant_id === fscVariantId;
  });

export const CartContainer = (props: Props) => {
  const { newProduct, productToRemove, productToChange, cartState, dispatch } =
    props;
  const {
    currentOrder,
    lineItems,
    isOpen,
    shouldShowPromoInput,
    promoInputValue,
  } = cartState;

  const [isUpgrading, setIsUpgrading] = useState(false);
  // Feature Flags
  const flags = useFlags();
  // we can use this boolean to toggle the upsell on/off
  const cartUpsellFlag = flags[Flags.CartUpsell];

  const isMobile = useMediaQuery(mediaQueries.forPhoneOnly);
  const [submittingPromo, setSubmittingPromo] = useState(false);
  const [showAltPayments, setShowAltPayments] = useState(false);

  const onCartTrigger = (response: {
    detail: {
      error: {
        type: string;
        errors: [];
      };
      keepClosed: boolean;
    };
  }) => {
    // May need to display errors in different places,
    // a type is included so that states can be distinguished etc
    const responseDetail = response && response.detail;
    if (responseDetail && responseDetail.error) {
      const {
        detail: {
          error: { type, errors },
        },
      } = response;

      switch (type) {
        case 'promoError':
          setPromoErrors(dispatch, errors);
          break;
        case 'cartError':
          setCartErrors(dispatch, errors);
          break;
        default:
          break;
      }
    }

    const updatedItems = getLineItems();
    const updatedOrder = getCurrentOrder();

    setLineItems(dispatch, updatedItems);
    setCurrentOrder(dispatch, updatedOrder);
    updateCart(updatedItems, updatedOrder);

    if (
      (!responseDetail && !isOpen) ||
      (responseDetail && !responseDetail.keepClosed && !isOpen)
    ) {
      openFloatCart();
    }
  };

  const onSetCartOpen = (response: {
    detail: {
      open: boolean;
    };
  }) => {
    const updatedItems = getLineItems();
    const updatedOrder = getCurrentOrder();

    setLineItems(dispatch, updatedItems);
    setCurrentOrder(dispatch, updatedOrder);
    updateCart(updatedItems, updatedOrder);

    const open = response && response.detail && response.detail.open;
    setIsOpen(dispatch, open);
  };

  const updateCart = (updatedItems: LineItem[], updatedOrder?: Order) => {
    let totalPrice;
    let subTotal;

    const items = updatedItems || lineItems;
    const order = updatedOrder || currentOrder;
    const productQuantity = items.reduce((sum, p) => sum + p.quantity, 0);

    if (order && order.promotions && order.promotions.length > 0) {
      totalPrice = parseFloat(order.total);
      subTotal = parseFloat(order.amount);
    } else {
      totalPrice = items.reduce((sum, p) => {
        if (p.base_price) sum += +p.base_price * p.quantity;
        return sum;
      }, 0);
      subTotal = totalPrice;
    }

    setCartTotal(dispatch, {
      productQuantity,
      totalPrice,
      subTotal,
      currencyId: 'USD',
      currencyFormat: '$',
    });
  };

  useEffect(() => {
    // initial cart load
    updateCart(lineItems);

    if (typeof window !== 'undefined') {
      // @ts-ignore
      window.addEventListener('onCartTrigger', onCartTrigger, false);
      // @ts-ignore
      window.addEventListener('onSetCartOpen', onSetCartOpen, false);
    }

    return () => {
      if (typeof window !== 'undefined') {
        // @ts-ignore
        window.removeEventListener('onCartTrigger', onCartTrigger, false);
        // @ts-ignore
        window.removeEventListener('onSetCartOpen', onSetCartOpen, false);
      }
      setIsOpen(dispatch, false);
    };
  }, []);

  useEffect(() => {
    newProduct && addProduct(newProduct);
  }, [newProduct]);

  useEffect(() => {
    productToRemove && removeProduct(productToRemove);
  }, [productToRemove]);

  useEffect(() => {
    productToChange && changeProductQuantity(productToChange);
  }, [productToChange, lineItems]);

  useEffect(() => {
    if (
      currentOrder &&
      currentOrder.promotions &&
      currentOrder.promotions.length > 0
    ) {
      setIsPromoApplied(dispatch, true);
      setCurrentPromoCode(dispatch, currentOrder.promotions[0].code);
    } else {
      setIsPromoApplied(dispatch, false);
    }
  }, [currentOrder]);

  // ---REMOVE hasLabVisitItemInCart LOGIC WHEN EXPRESS PAY IN BDC CARTS IS FIXED (below this line)---
  const showAltPaymentButtons =
    !hasSubscriptionItemInCart(lineItems) && !hasLabVisitItemInCart(lineItems);

  // Show alternative payment buttons (PayPal/Express Pay)
  useEffect(() => {
    setShowAltPayments(showAltPaymentButtons);
  }, [lineItems]);
  // ---REMOVE hasLabVisitItemInCart LOGIC WHEN EXPRESS PAY IN BDC CARTS IS FIXED (above this line)---

  const resetCart = () => {
    setShouldShowPromoInput(dispatch, false);
    setPromoInputValue(dispatch, '');
    setPromoErrors(dispatch, null);
    setCartErrors(dispatch, null);
  };

  const openFloatCart = () => {
    if (isMobile) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
    }
    setIsOpen(dispatch, true);
  };

  const closeFloatCart = () => {
    document.body.style.overflow = '';
    document.body.style.height = '';
    setIsOpen(dispatch, false);
    resetCart();
  };

  const addProduct = (product: LineItem) => {
    let productAlreadyInCart = false;

    lineItems.forEach((item: LineItem) => {
      if (item.variant_id === product.variant_id) {
        item.quantity += product.quantity;
        productAlreadyInCart = true;
      }
    });

    if (!productAlreadyInCart) {
      lineItems.push(product);
      addLineItem(product);
    }

    updateCart(lineItems);
    openFloatCart();
    setCartErrors(dispatch, null);
  };

  const removeProduct = (product: LineItem) => {
    const index = lineItems.findIndex(
      (p: LineItem) => p.variant_id === product.variant_id,
    );
    if (index >= 0) {
      lineItems.splice(index, 1);
      removeLineItem(product);
    }

    updateCart(lineItems);
    setCartErrors(dispatch, null);
  };

  const changeProductQuantity = (changedProduct: LineItem) => {
    const product = lineItems.find(
      (p: LineItem) => p.variant_id === changedProduct.variant_id,
    );
    if (product) {
      product.quantity = changedProduct.quantity;
      if (product.quantity <= 0) {
        removeProduct(product);
      }
      editLineItem(product);
      updateCart(lineItems);
      setCartErrors(dispatch, null);
    }
  };

  const handlePromoChange = (formfield: FormFieldType) => {
    const { value } = formfield;
    setPromoInputValue(dispatch, value);
  };

  const handlePromoLinkChange = () => {
    setShouldShowPromoInput(dispatch, !shouldShowPromoInput);
  };

  const handlePromoApply = (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmittingPromo(true);
    addPromoToOrder(currentOrder.id, promoInputValue).finally(() =>
      setSubmittingPromo(false),
    );
    setPromoErrors(dispatch, null);
  };

  const handleRemovePromo = () => {
    removePromos(currentOrder.id);
    setPromoErrors(dispatch, null);
    setPromoInputValue(dispatch, '');
  };

  const setTOS = (isSet: boolean) => {
    setShowTOS(dispatch, isSet);
  };

  const cartProps = {
    cartState,
    removeProduct,
    changeProductQuantity,
    handlePromoChange,
    handlePromoLinkChange,
    handlePromoApply,
    handleRemovePromo,
    setTOS,
    openFloatCart,
    closeFloatCart,
    submittingPromo,
    showAltPayments,
  };

  return (
    <StaticQuery
      query={graphql`
        query {
          product: spreeProduct(
            slug: { eq: "food-sensitivity-comprehensive-test" }
          ) {
            variants {
              id
              is_master
            }
          }
        }
      `}
      render={(data) => {
        const { product: fscProduct } = data;

        // Get the FSC master variant from the Spree Product
        const fscVariant = fscProduct?.variants?.find((variant: Variant) => {
          return variant.is_master;
        });

        const handleUpgrade = (): void => {
          // turn on loading
          setIsUpgrading(true);

          // Get the FS lineItem from the current cart's line items
          const fsLineItem = getFSLineItem(lineItems);

          if (!fsLineItem?.variant_id || !fscVariant.id) {
            throw 'missing a variant needed to upgrade';
          }

          upgradeLineItem(
            fsLineItem?.variant_id,
            fscVariant.id,
            undefined,
            () => {
              setIsUpgrading(false);
              ewAnalytics.track({
                event: ANALYTICS.EVENTS.CLICKED_BUTTON,
                data: {
                  label: ANALYTICS.LABELS.FS_TO_FSC_CART_UPGRADE,
                },
              });
            },
          );
        };

        // This is a hack related to EWD-13664. This is temporary code that allows us to
        // display the upsell card in the cart when Food Sensitivity is detected in the cart
        // we should remove this as soon as we can add the product slug to the line item schema
        const includesFS = includesFSLineItem(lineItems);
        const includesFSC = includesFSCLineItem(lineItems, fscVariant.id);

        const showUpsell = cartUpsellFlag && includesFS && !includesFSC;

        const upsellCard = {
          ...upsellCardContent,
          handleOnClick: handleUpgrade,
        };

        return (
          <Cart
            {...cartProps}
            upsellCard={showUpsell ? upsellCard : undefined}
            showLoading={isUpgrading}
          />
        );
      }}
    />
  );
};

export default CartContainer;
